import { ExternalLink, Mail, CreditCard, Landmark, FileCheck2, RotateCcw } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDateTime } from '@/lib/utils';
import { useInvoiceArEvidence, type ArEvidenceEvent } from '../hooks/useInvoiceArEvidence';

interface InvoiceHistoryTabProps {
  invoiceNumber: string;
  enabled: boolean;
}

const GMAIL_BASE = 'https://mail.google.com/mail/u/0/#all/';

function humanizeOutcome(outcome: string | null): string {
  if (!outcome) return 'Reconciliation attempt';
  return outcome.charAt(0).toUpperCase() + outcome.slice(1).replace(/_/g, ' ');
}

function EventRow({ ev }: { ev: ArEvidenceEvent }) {
  return (
    <li className="relative pl-6 pb-5 last:pb-0">
      <span className="absolute left-0 top-1.5 h-2 w-2 rounded-full bg-primary" aria-hidden="true" />
      <span className="absolute left-[3px] top-3.5 bottom-0 w-px bg-border" aria-hidden="true" />
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-sm font-medium text-foreground">{humanizeOutcome(ev.outcome)}</p>
        <time className="text-xs text-muted-foreground tabular-nums shrink-0">{formatDateTime(ev.ts)}</time>
      </div>
      {ev.notes && <p className="text-xs text-muted-foreground mt-0.5">{ev.notes}</p>}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs">
        {ev.gmailMessageId && (
          <a
            href={`${GMAIL_BASE}${ev.gmailMessageId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            <Mail className="h-3 w-3" aria-hidden="true" /> View email <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </a>
        )}
        {ev.qbPaymentId && (
          <span className="inline-flex items-center gap-1 text-success-dark">
            <CreditCard className="h-3 w-3" aria-hidden="true" /> QB payment {ev.qbPaymentId}
          </span>
        )}
        {(ev.stripeChargeId || ev.stripePaymentIntentId) && (
          <span className="inline-flex items-center gap-1 text-secondary-foreground">
            <Landmark className="h-3 w-3" aria-hidden="true" /> Stripe {ev.stripeChargeId || ev.stripePaymentIntentId}
          </span>
        )}
        {ev.docLinked && (
          <span className="inline-flex items-center gap-1 text-muted-foreground">
            <FileCheck2 className="h-3 w-3" aria-hidden="true" /> Doc linked
          </span>
        )}
        {ev.invoiceStatus && (
          <span className="text-muted-foreground">status: {ev.invoiceStatus}</span>
        )}
        {ev.balance != null && (
          <span className="text-muted-foreground tabular-nums">bal: ${ev.balance.toFixed(2)}</span>
        )}
        {ev.retryCount != null && ev.retryCount > 0 && (
          <span className="inline-flex items-center gap-1 text-muted-foreground">
            <RotateCcw className="h-3 w-3" aria-hidden="true" /> retry {ev.retryCount}
          </span>
        )}
      </div>
    </li>
  );
}

export function InvoiceHistoryTab({ invoiceNumber, enabled }: InvoiceHistoryTabProps) {
  const { data, isLoading, isError, refetch } = useInvoiceArEvidence(invoiceNumber, enabled);

  if (isLoading) {
    return (
      <div className="space-y-3 mt-4" aria-busy="true" aria-label="Loading invoice history">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mt-4 text-sm text-destructive space-y-2">
        <p>Failed to load reconciliation history for this invoice.</p>
        <button
          type="button"
          onClick={() => refetch()}
          className="underline text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
        >
          Try again
        </button>
      </div>
    );
  }

  const events = data?.events ?? [];

  if (events.length === 0) {
    return (
      <div className="mt-4 text-sm text-muted-foreground text-center py-8 border rounded-lg">
        No reconciliation history recorded for this invoice yet.
      </div>
    );
  }

  return (
    <div className="mt-4 overflow-y-auto max-h-[60vh] border rounded-lg p-4 bg-card">
      <ol className="list-none">
        {events.map((ev, i) => (
          <EventRow key={`${ev.ts}-${i}`} ev={ev} />
        ))}
      </ol>
    </div>
  );
}
